import { isNil } from 'lodash';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Group } from '../containers/Group/Group.component';
import { StyledIndicator, StyledTabsGroup, StyledTabsList } from './Tabs.styles';
import { IStyledIndicatorProps, ITabProps, ITabsProps } from './interfaces';

export const Tabs: React.FunctionComponent<ITabsProps> = ({
  size = 's',
  selectedTab,
  children: childrenProp,
  onChange,
  borderless,
  subject,
  vertical = false,
  showIndicator = true,
  indicatorGradiant = false,
  tabOffset,
  fullWidth,
}) => {
  const [{ top, left, width, height }, setIndicatorProps] = useState({ height: 0, width: 0, top: 0, left: 0 });
  const tabListRef = useRef<HTMLDivElement | null>(null);
  const selectedTabRef = useRef<HTMLDivElement | null>(null);

  const calculateIndicatorProps = (): IStyledIndicatorProps => {
    let selectedTabMetadata, tabsMetadata;

    if (tabListRef) {
      const containerRect = tabListRef.current?.getBoundingClientRect();

      tabsMetadata = {
        left: containerRect?.left,
        top: containerRect?.top,
      };

      selectedTabMetadata = selectedTabRef.current && selectedTabRef.current.getBoundingClientRect();
    }

    let calculatedLeft = 0;
    let calculateTop = 0;

    if (selectedTabMetadata && tabsMetadata) {
      calculatedLeft = Math.round(selectedTabMetadata.left - tabsMetadata.left);
      calculateTop = Math.round(selectedTabMetadata.top - tabsMetadata.top);
    }

    return {
      top: calculateTop,
      left: calculatedLeft,
      height: selectedTabMetadata ? Math.round(selectedTabMetadata.height) : 0,
      width: selectedTabMetadata ? Math.round(selectedTabMetadata.width) : 0,
    };
  };

  const handleHelperChange = () => setIndicatorProps(calculateIndicatorProps());

  const children = React.Children.map(childrenProp as ReactElement<ITabProps>[], (tab, index) => {
    if (!React.isValidElement(tab)) return null;

    const tabCount = React.Children.count(childrenProp);
    const tabValue = isNil(tab.props.value) ? index : tab.props.value;

    const selected = selectedTab === tabValue;

    const childRef = selected ? { ref: selectedTabRef } : {};

    return React.cloneElement(tab, {
      size,
      selected,
      width: fullWidth ? `${100 / tabCount}%` : undefined,
      onClick: onChange,
      value: tabValue,
      onHelperChange: handleHelperChange,
      ...childRef,
    } as any);
  });

  useEffect(() => {
    setIndicatorProps(calculateIndicatorProps());
  }, [selectedTab]);

  return (
    <StyledTabsGroup
      gap={0}
      vertical={!vertical}
      fullWidth={!vertical}
      fullHeight={vertical}
      {...(vertical ? { overflow: 'scroll', alignItems: 'flex-start' } : {})}
    >
      <StyledTabsList
        tabOffset={tabOffset}
        ref={tabListRef}
        borderless={borderless}
        subject={subject}
        vertical={vertical}
      >
        <Group width={'100%'} gap={indicatorGradiant ? 0 : 2} vertical={vertical}>
          {children}
        </Group>
      </StyledTabsList>
      {showIndicator && <StyledIndicator {...{ top, left, width, height, borderless, vertical, indicatorGradiant }} />}
    </StyledTabsGroup>
  );
};

Tabs.displayName = 'Tabs';
